import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core';
import CopyClipboard from '@material-ui/icons/AttachFile';
import { useSnackbar } from 'notistack';
import CheckOutlinedIcon from '@material-ui/icons/CheckOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import MovieOutlinedIcon from '@material-ui/icons/MovieOutlined';
import TvOutlinedIcon from '@material-ui/icons/TvOutlined';
import * as userService from 'services/user';
import {
  findGroupContainingRecords,
  finalizeNonDuplicateGroups,
  hydrateGroupRecords,
  mergeOverlappingNonDuplicateGroups,
  normalizeNonDuplicateGroups,
  rebuildDuplicateGroupsAsync,
  releaseSupersededNonDuplicateGroups,
} from './duplicate-spotter.utils';
import { formatGroupName } from './utils';
import {
  DuplicateSpotterDateRange,
  DuplicateSpotterProgress,
  getContentForDuplicateSpotter,
  getNotDuplicateDecisions,
  markAsNotDuplicate,
} from '@api/duplicate-spotter';
import { dateToYmdStr, readableDate, YmdStrToDate } from '@lib/common';
import { subDays } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { PageHeader } from './page-header';
import { SummaryBar } from './summary-bar';
import { FilterBar, LocalSearchBar } from './filter-bar';
import { DuplicateGroupCard } from './duplicate-group-card';
import { AppBarTitle } from '@components/app-bar-title';
import { RiSearchFill } from 'react-icons/ri';
import { ContentRecord, DuplicateDecisionPayload, DuplicateGroup, MATCH_REASON_LABELS, MatchReason } from './types';
import { NonDuplicateGroupsTab } from './non-duplicate-groups';
/* ============================================================
   CONSTANTS
   ============================================================ */

const GROUPS_PER_PAGE = 10;
const MAPPED_GROUPS_STORAGE_KEY = 'duplicate-spotter-mapped-groups';
const DEFAULT_SCAN_DAYS = 30;

const isValidDate = (value: Date | null): value is Date =>
  value instanceof Date && !Number.isNaN(value.getTime());

const createDefaultScanDates = () => {
  const endDate = new Date();
  return {
    startDate: subDays(endDate, DEFAULT_SCAN_DAYS),
    endDate,
  };
};

const toDateRange = (startDate: Date, endDate: Date): DuplicateSpotterDateRange => ({
  startDate: dateToYmdStr(startDate),
  endDate: dateToYmdStr(endDate),
});

const readMappedGroupKeys = (): string[] => {
  try {
    const raw = sessionStorage.getItem(MAPPED_GROUPS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const getGroupLockKey = (group: DuplicateGroup) =>
  group.groupKey || group.id;

const groupMatchesSearch = (group: DuplicateGroup, query: string) => {
  const needle = query.trim().toLowerCase();

  if (!needle) {
    return true;
  }

  const needleId = needle.replace(/^tt/, '');

  return group.records.some(record => {
    const titles = [
      record.title,
      record.original_title,
      record.content_aka,
      record.working_title,
    ];

    if (titles.some(title => (title || '').toLowerCase().includes(needle))) {
      return true;
    }

    const imdbId = (record.imdb_id || '').toLowerCase();

    return imdbId.includes(needle) || imdbId.replace(/^tt/, '').includes(needleId);
  });
};


/* ============================================================
   COMPONENT
   ============================================================ */

const DuplicateSpotter: React.FC = () => {
  const history = useHistory();
  /* ----------------------------------------------------------
     API DATA
     ---------------------------------------------------------- */
const { enqueueSnackbar } = useSnackbar();
  const [contentRecords, setContentRecords] = useState<ContentRecord[]>([]);

  const [duplicateGroups, setDuplicateGroups] = useState<DuplicateGroup[]>([]);
  const [mappedGroupKeys, setMappedGroupKeys] = useState<string[]>(() =>
    readMappedGroupKeys()
  );

  const [loading, setLoading] = useState(false);

  const [duplicateProgress, setDuplicateProgress] =
    useState<DuplicateSpotterProgress>({
      processed: 0,
      total: undefined,
      remaining: undefined,
      page: 0,
      status: 'Preparing duplicate scan...',
    });
  const [lastScan, setLastScan] = useState<Date | null>(null);

  /* ----------------------------------------------------------
     FILTERS
     ---------------------------------------------------------- */

  const [matchType, setMatchType] = useState<string>('ALL');

  const [contentType, setContentType] = useState<string>('ALL');

  const [searchQuery, setSearchQuery] = useState('');

  const [scanDates, setScanDates] = useState(createDefaultScanDates);

  const [appliedDateRange, setAppliedDateRange] = useState<DuplicateSpotterDateRange>(() =>
    toDateRange(scanDates.startDate, scanDates.endDate)
  );


  /* ----------------------------------------------------------
     GROUP PAGINATION
     ---------------------------------------------------------- */

  const [visibleGroupCount, setVisibleGroupCount] = useState<number>(GROUPS_PER_PAGE);

  /* ----------------------------------------------------------
     EXPANDED GROUPS
     ---------------------------------------------------------- */

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [pendingNdChanges, setPendingNdChanges] = useState<
      DuplicateGroup[]
    >([]);
  //----------------------------------------------------------
  // Tabs Data
  //----------------------------------------------------------
  const DUPLICATE_TAB = 0;
  const NON_DUPLICATE_TAB = 1;
  const [nonDuplicateGroups, setNonDuplicateGroups] = useState<DuplicateGroup[]>([]);

  const [nonDuplicateLoading, setNonDuplicateLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(DUPLICATE_TAB);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
const [pendingTab, setPendingTab] = useState<number | null>(null);
  const [
  expandedNonDuplicateGroups,
  setExpandedNonDuplicateGroups,
] = useState<Record<string, boolean>>({});
  const [savedNonDuplicateGroups, setSavedNonDuplicateGroups] =
  useState<DuplicateGroup[]>([]);
  const [processingGroupId, setProcessingGroupId] = useState<string | null>(
    null
  );
  const hasUnsavedChanges = pendingNdChanges.length > 0;

  const handleTabChange = (
  event: React.ChangeEvent<{}>,
  newValue: number
) => {
  if (
    activeTab === NON_DUPLICATE_TAB &&
    newValue === DUPLICATE_TAB &&
    hasUnsavedChanges
  ) {
    setPendingTab(newValue);
    setShowSaveDialog(true);
    return;
  }

  setActiveTab(newValue);
};

const handleSaveAndSwitch = async () => {
  try {
    await saveNonDuplicateChanges();

    setShowSaveDialog(false);

    if (pendingTab !== null) {
      setActiveTab(pendingTab);
    }

    setPendingTab(null);
  } catch (error) {
    console.error('Failed to save non-duplicate changes', error);
  }
};

const handleCancelSave = () => {
  setShowSaveDialog(false);
  setPendingTab(null);
};

const handleToggleNonDuplicateGroup = (
  groupId: string
) => {
  setExpandedNonDuplicateGroups(previous => ({
    ...previous,
    [groupId]: !previous[groupId],
  }));
};

const loadNonDuplicateGroups = async () => {
  try {
    setNonDuplicateLoading(true);

    const response = await getNotDuplicateDecisions();

    console.log('Duplicate decisions response:', response);

    const decisions = response?.data?.decisions || [];

    const groups = hydrateGroupRecords(
      normalizeNonDuplicateGroups(decisions),
      contentRecords
    );

    setNonDuplicateGroups(groups);
    setSavedNonDuplicateGroups(groups);
    setPendingNdChanges([]);
  } catch (error) {
    console.error('Failed to load non-duplicate groups', error);
    setNonDuplicateGroups([]);
  } finally {
    setNonDuplicateLoading(false);
  }
};

useEffect(() => {
  if (
    activeTab === NON_DUPLICATE_TAB &&
    nonDuplicateGroups.length === 0
  ) {
    loadNonDuplicateGroups();
  }
}, [activeTab]);
  /* ==========================================================
     LOAD DATA
     ========================================================== */

const loadDuplicateSpotterData = useCallback(async () => {
  try {
    setLoading(true);

    setDuplicateProgress({
      processed: 0,
      total: undefined,
      remaining: undefined,
      page: 0,
      status: 'Starting duplicate scan...',
    });

    const decisionsPromise = getNotDuplicateDecisions().catch(error => {
      console.error(
        '[Duplicate Spotter] Failed to load non-duplicate decisions',
        error
      );

      return { data: { decisions: [] } };
    });

    const records = await getContentForDuplicateSpotter(
      (progress) => {
        setDuplicateProgress(progress);
      },
      appliedDateRange
    );

    if (!Array.isArray(records)) {
      console.error(
        '[Duplicate Spotter] Expected array but received:',
        records
      );

      setContentRecords([]);
      setDuplicateGroups([]);

      return;
    }

    setDuplicateProgress((prev) => ({
      ...prev,
      processed: records.length,
      total: records.length,
      remaining: 0,
      status: `Loaded ${records.length} records. Analyzing for duplicates...`,
    }));

    const response = await decisionsPromise;
    const nonDuplicateDecisions = response?.data?.decisions || [];
    const loadedNonDuplicateGroups = hydrateGroupRecords(
      normalizeNonDuplicateGroups(nonDuplicateDecisions),
      records
    );

    const finalDuplicateGroups = await rebuildDuplicateGroupsAsync(
      records,
      {
        nonDuplicateGroups: loadedNonDuplicateGroups,
      },
      (processed, total) => {
        setDuplicateProgress(prev => ({
          ...prev,
          processed,
          total,
          remaining: Math.max(total - processed, 0),
          status: `Analyzing duplicates... ${processed.toLocaleString()} of ${total.toLocaleString()} records`,
        }));
      }
    );

    const visibleNonDuplicateGroups = finalizeNonDuplicateGroups(
      releaseSupersededNonDuplicateGroups(
        loadedNonDuplicateGroups,
        finalDuplicateGroups
      )
    );

    setNonDuplicateGroups(visibleNonDuplicateGroups);
    setSavedNonDuplicateGroups(visibleNonDuplicateGroups);
    setPendingNdChanges([]);

    setContentRecords(records);
    setDuplicateGroups(finalDuplicateGroups);

    setDuplicateProgress((prev) => ({
      ...prev,
      processed: records.length,
      total: records.length,
      remaining: 0,
      status: `Analysis complete. Found ${finalDuplicateGroups.length} potential duplicate groups.`,
    }));

    const reopenedGroup = finalDuplicateGroups[0];
    const wasReopened = Boolean(
      reopenedGroup &&
      loadedNonDuplicateGroups.some(group =>
        group.records.some(record =>
          reopenedGroup.records.some(
            item => String(item.vtr_id) === String(record.vtr_id)
          )
        )
      )
    );

    setVisibleGroupCount(GROUPS_PER_PAGE);
    setExpandedGroups(
      wasReopened && reopenedGroup ? { [reopenedGroup.id]: true } : {}
    );
    setLastScan(new Date());

  } catch (error) {
    console.error(
      '[Duplicate Spotter] Failed to load data:',
      error
    );

    setContentRecords([]);
    setDuplicateGroups([]);

  } finally {
    setLoading(false);
  }
}, [appliedDateRange]);
  /* ==========================================================
     INITIAL LOAD
     ========================================================== */

  useEffect(() => {
    loadDuplicateSpotterData();
  }, [loadDuplicateSpotterData]);

  /* ==========================================================
     FILTERED GROUPS
     ========================================================== */

  const filteredGroups = useMemo(() => {
    return duplicateGroups.filter(group => {
      /* --------------------------------------------------
             MATCH TYPE
             -------------------------------------------------- */

      if (matchType !== 'ALL') {
        const hasMatch = group.matchReasons.includes(matchType as MatchReason);

        if (!hasMatch) {
          return false;
        }
      }

      /* --------------------------------------------------
             CONTENT TYPE
             -------------------------------------------------- */

      if (contentType !== 'ALL') {
        const selectedType = Number(contentType);

        const hasContentType = group.records.some(record => record.content_type === selectedType);

        if (!hasContentType) {
          return false;
        }
      }

      return groupMatchesSearch(group, searchQuery);
    });
  }, [duplicateGroups, matchType, contentType, searchQuery]);

  /* ==========================================================
     RESET LOAD MORE WHEN FILTER CHANGES
     ========================================================== */

  useEffect(() => {
    setVisibleGroupCount(GROUPS_PER_PAGE);
  }, [matchType, contentType, searchQuery]);

  /* ==========================================================
     VISIBLE GROUPS
     ========================================================== */

  const visibleGroups = useMemo(() => {
    return filteredGroups.slice(0, visibleGroupCount);
  }, [filteredGroups, visibleGroupCount]);

  const hasMoreGroups = visibleGroupCount < filteredGroups.length;

  /* ==========================================================
     LOAD MORE
     ========================================================== */

  const handleLoadMoreGroups = () => {
    setVisibleGroupCount(previousCount =>
      Math.min(previousCount + GROUPS_PER_PAGE, filteredGroups.length),
    );
  };

  /* ==========================================================
     TOGGLE GROUP
     ========================================================== */

  const handleToggleGroup = (groupId: string) => {
    setExpandedGroups(previous => ({
      ...previous,

      [groupId]: !previous[groupId],
    }));
  };

  /* ==========================================================
     NOT DUPLICATE
     ========================================================== */
const handleNotDuplicate = async (
  group: DuplicateGroup,
  selectedIds: string[]
) => {
  try {
 if (selectedIds.length === 0) {
  enqueueSnackbar('Please select at least two content', {
    variant: 'warning',
  });
  return;
}

if (selectedIds.length < 2) {
  enqueueSnackbar('Please select at least two contents', {
    variant: 'warning',
  });
  return;
}

    const selectedIdSet = new Set(selectedIds.map(String));

    const selectedRecords = group.records.filter(
      record => selectedIdSet.has(String(record.vtr_id))
    );

    const sourceGroupVtrIds = group.records
      .map(record => String(record.vtr_id))
      .filter(Boolean);

    const contentData = group.records.reduce(
      (acc, record) => {
        acc[record.vtr_id] = {
          title: record.title || '',
          original_title: record.original_title || '',
          content_aka: record.content_aka || '',
          working_title: record.working_title || '',
          imdb_id: record.imdb_id || '',
          content_type: record.content_type ?? null,
        };

        return acc;
      },
      {} as Record<string, any>
    );

    const user = userService.getUser();
    const decidedAt = new Date().toISOString();
    const payload: DuplicateDecisionPayload = {
      decisions: [
        {
          selected_vtr_ids: selectedIds,
          match_reasons: (group.matchReasons || []).map(
            reason => MATCH_REASON_LABELS[reason] || reason
          ),
          group_vtr_ids: sourceGroupVtrIds,
          content_data: contentData,
          created_by: user?.vtr_id || user?.username,
          created_at: decidedAt,
        },
      ],
    };

    setProcessingGroupId(group.id);
    enqueueSnackbar(
      'Moving selected content to a non-duplicate group...',
      { variant: 'info' }
    );

    await markAsNotDuplicate(payload);

    const remainingRecords = group.records.filter(
      record => !selectedIdSet.has(String(record.vtr_id))
    );

    const nextNonDuplicateGroup: DuplicateGroup = {
      id: 'NDG-NEW',
      groupKey: [...selectedIds].map(String).sort().join('|'),
      records: selectedRecords,
      matchReasons: group.matchReasons,
      createdBy: user?.vtr_id || user?.username,
      createdAt: decidedAt,
      sourceGroupVtrIds,
    };

    const nextNonDuplicateGroups = mergeOverlappingNonDuplicateGroups([
      nextNonDuplicateGroup,
      ...nonDuplicateGroups,
    ]);

    setNonDuplicateGroups(nextNonDuplicateGroups);
    setExpandedNonDuplicateGroups({
      [nextNonDuplicateGroups[0].id]: true,
    });
    setActiveTab(NON_DUPLICATE_TAB);

    const nextDuplicateGroups = await rebuildDuplicateGroupsAsync(
      contentRecords,
      {
        nonDuplicateGroups: nextNonDuplicateGroups,
        extraRecords: remainingRecords,
        previousGroups: duplicateGroups,
      }
    );

    setDuplicateGroups(nextDuplicateGroups);

    enqueueSnackbar(
      `Moved to ${nextNonDuplicateGroups[0].id}`,
      { variant: 'success' }
    );
  } catch (error) {
    console.error(
      'Failed to mark group as non-duplicate',
      error
    );

    enqueueSnackbar('Failed to mark as not duplicate', {
      variant: 'error',
    });
  } finally {
    setProcessingGroupId(null);
  }
};

const handleMarkDuplicate = async (
  group: DuplicateGroup
) => {
  setProcessingGroupId(group.id);
  enqueueSnackbar(
    'Moving non-duplicate group to a duplicate group...',
    { variant: 'info' }
  );

  try {
    const remainingNonDuplicateGroups = finalizeNonDuplicateGroups(
      nonDuplicateGroups.filter(item =>
        item.id !== group.id && item.groupKey !== group.groupKey
      )
    );

    setNonDuplicateGroups(remainingNonDuplicateGroups);

    setPendingNdChanges(previous => {
      if (previous.some(item => item.id === group.id)) {
        return previous;
      }

      return [...previous, group];
    });

    const nextDuplicateGroups = await rebuildDuplicateGroupsAsync(
      contentRecords,
      {
        nonDuplicateGroups: remainingNonDuplicateGroups,
        extraRecords: group.records,
        forceIncludeRecords: group.records,
        previousGroups: duplicateGroups,
      }
    );

    const targetGroup = findGroupContainingRecords(
      nextDuplicateGroups,
      group.records.map(record => record.vtr_id)
    );

    const orderedDuplicateGroups = targetGroup
      ? [
          targetGroup,
          ...nextDuplicateGroups.filter(item => item.id !== targetGroup.id),
        ]
      : nextDuplicateGroups;

    setDuplicateGroups(orderedDuplicateGroups);
    setVisibleGroupCount(GROUPS_PER_PAGE);

    if (targetGroup) {
      setExpandedGroups({
        [targetGroup.id]: true,
      });

      enqueueSnackbar(
        `Moved to ${formatGroupName(targetGroup.id)} at the top of Duplicate Groups`,
        { variant: 'success' }
      );
    } else {
      enqueueSnackbar('Marked as duplicate', {
        variant: 'success',
      });
    }
  } catch (error) {
    console.error('Failed to mark group as duplicate', error);

    enqueueSnackbar('Failed to mark as duplicate', {
      variant: 'error',
    });
  } finally {
    setProcessingGroupId(null);
  }
};

const persistNonDuplicateGroups = async (
  groups: DuplicateGroup[]
) => {
  const decisions = groups.map(group => ({
    selected_vtr_ids: group.records.map(record => record.vtr_id),
    match_reasons: group.matchReasons || [],
    group_vtr_ids: group.sourceGroupVtrIds?.length
      ? group.sourceGroupVtrIds
      : group.records.map(record => record.vtr_id),
    content_data: group.records.reduce((acc, record) => {
      acc[record.vtr_id] = {
        title: record.title || '',
        original_title: record.original_title || '',
        content_aka: record.content_aka || '',
        working_title: record.working_title || '',
        imdb_id: record.imdb_id || '',
        content_type: record.content_type ?? null,
      };

      return acc;
    }, {} as Record<string, any>),
    created_by: group.createdBy,
    created_at: group.createdAt,
  }));

  await markAsNotDuplicate({
    decisions,
    sync: true,
  });

  setPendingNdChanges([]);
};

const saveNonDuplicateChanges = async (
  groups: DuplicateGroup[] = nonDuplicateGroups
) => {
  try {
    await persistNonDuplicateGroups(groups);
    setSavedNonDuplicateGroups(groups);

    enqueueSnackbar('Changes saved successfully', {
      variant: 'success',
    });
  } catch (error) {
    console.error('Failed to save Not Duplicate changes', error);

    enqueueSnackbar('Failed to save changes', {
      variant: 'error',
    });

    throw error;
  }
};

  /* ==========================================================
     MERGE SELECTED
     ========================================================== */

  const handleMergeSelected = (group: DuplicateGroup, selectedIds: string[]) => {
    if (selectedIds.length < 2) {
      enqueueSnackbar('Please select at least two contents', {
        variant: 'warning',
      });
      return;
    }

    const mergeUrl = `/content/merge/${selectedIds.join(',')}`;
    window.open(mergeUrl, '_blank', 'noopener,noreferrer');

    const lockKey = getGroupLockKey(group);
    setMappedGroupKeys(prev => {
      if (prev.includes(lockKey)) {
        return prev;
      }
      const next = [...prev, lockKey];
      sessionStorage.setItem(MAPPED_GROUPS_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  /* ==========================================================
     CLEAR FILTERS
     ========================================================== */

  const hasActiveFilters =
    matchType !== 'ALL' || contentType !== 'ALL' || Boolean(searchQuery.trim());

  const filteredNonDuplicateGroups = useMemo(
    () => nonDuplicateGroups.filter(group => groupMatchesSearch(group, searchQuery)),
    [nonDuplicateGroups, searchQuery]
  );

  const canApplyDateRange =
    isValidDate(scanDates.startDate) &&
    isValidDate(scanDates.endDate) &&
    dateToYmdStr(scanDates.startDate) <= dateToYmdStr(scanDates.endDate);

  const applyDateRange = () => {
    if (!canApplyDateRange) {
      return;
    }

    const nextRange = toDateRange(scanDates.startDate, scanDates.endDate);

    setAppliedDateRange(current =>
      current.startDate === nextRange.startDate && current.endDate === nextRange.endDate
        ? current
        : nextRange
    );
  };

  const createdRangeLabel = `Created ${readableDate(
    YmdStrToDate(appliedDateRange.startDate)
  )} – ${readableDate(YmdStrToDate(appliedDateRange.endDate))}`;

  const clearFilters = () => {
    setMatchType('ALL');
    setContentType('ALL');
    setSearchQuery('');
  };

 /* ==========================================================
   RENDER
   ========================================================== */

return (
  <>
    <AppBarTitle>
      <IconButton color="inherit">
        <RiSearchFill />
      </IconButton>{' '}
      Content Duplicate Spotter
    </AppBarTitle>

    <Box
      style={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        background: '#F6F8FC',
        overflow: 'hidden',
      }}
    >
      {/* ======================================================
          PAGE HEADER
          ====================================================== */}

      <PageHeader
        loading={
          activeTab === DUPLICATE_TAB
            ? loading
            : nonDuplicateLoading
        }
        onRefresh={
          activeTab === DUPLICATE_TAB
            ? loadDuplicateSpotterData
            : loadNonDuplicateGroups
        }
      />

      {/* ======================================================
          TABS
          ====================================================== */}

      <Box
        style={{
          background: '#FFFFFF',
          borderBottom: '1px solid #E5E7EB',
          padding: '0 20px',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label={`Duplicate Groups`} />

          <Tab label={`Non-Duplicate Groups`} />
        </Tabs>
      </Box>

      <LocalSearchBar value={searchQuery} onChange={setSearchQuery} />

      {/* ======================================================
          DUPLICATE GROUPS TAB
          ====================================================== */}

      {activeTab === DUPLICATE_TAB && (
        <>
          <SummaryBar
            lastScan={lastScan}
            duplicateCount={filteredGroups.length}
            recordCount={contentRecords.length}
            createdRangeLabel={createdRangeLabel}
          />

          <FilterBar
            matchType={matchType}
            contentType={contentType}
            startDate={scanDates.startDate}
            endDate={scanDates.endDate}
            onStartDateChange={date =>
              setScanDates(current => ({ ...current, startDate: date }))
            }
            onEndDateChange={date =>
              setScanDates(current => ({ ...current, endDate: date }))
            }
            onApplyDateRange={applyDateRange}
            canApplyDateRange={canApplyDateRange}
            dateRangeDisabled={loading}
            onMatchTypeChange={setMatchType}
            onContentTypeChange={setContentType}
            onClear={clearFilters}
            hasActiveFilters={hasActiveFilters}
          />

          <Box
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: '4px 20px 20px',
            }}
          >
            {/* LOADING */}

            {loading && (
              <LoadingState progress={duplicateProgress} />
            )}

            {/* EMPTY */}

            {!loading && filteredGroups.length === 0 && (
              <EmptyState
                hasFilters={hasActiveFilters}
                onClear={clearFilters}
              />
            )}

            {/* GROUPS */}

            {!loading && filteredGroups.length > 0 && (
              <>
                {visibleGroups.map(group => (
                  <DuplicateGroupCard
                    key={group.id}
                    group={group}
                    expanded={expandedGroups[group.id] === true}
                    onToggle={() =>
                      handleToggleGroup(group.id)
                    }
                        mode="duplicate"
                    onNotDuplicate={handleNotDuplicate}
                    onMerge={handleMergeSelected}
                    actionLoading={processingGroupId === group.id}
                    isMapped={mappedGroupKeys.includes(getGroupLockKey(group))}
                  />
                ))}

                {hasMoreGroups && (
                  <LoadMoreButton
                    remainingCount={
                      filteredGroups.length -
                      visibleGroupCount
                    }
                    onClick={handleLoadMoreGroups}
                  />
                )}
              </>
            )}
          </Box>
        </>
      )}

      {/* ======================================================
          NON-DUPLICATE GROUPS TAB
          ====================================================== */}

    {activeTab === NON_DUPLICATE_TAB && (
  <NonDuplicateGroupsTab
    groups={filteredNonDuplicateGroups}
    hasSearch={Boolean(searchQuery.trim())}
    onClearSearch={() => setSearchQuery('')}
    loading={nonDuplicateLoading}
    expandedGroups={expandedNonDuplicateGroups}
    onToggleGroup={handleToggleNonDuplicateGroup}
    onMarkDuplicate={handleMarkDuplicate}
    processingGroupId={processingGroupId}
  />
)}
<Dialog
  open={showSaveDialog}
  onClose={handleCancelSave}
  aria-labelledby="save-changes-dialog-title"
  aria-describedby="save-changes-dialog-description"
>
  <DialogTitle id="save-changes-dialog-title">
    Save Changes?
  </DialogTitle>

  <DialogContent>
    <DialogContentText id="save-changes-dialog-description">
      You have unsaved changes. Do you want to save the changes before
      switching to Duplicate?
    </DialogContentText>
  </DialogContent>

  <DialogActions>
    <Button
      onClick={handleCancelSave}
      color="default"
    >
      Cancel
    </Button>

    <Button
      onClick={handleSaveAndSwitch}
      color="primary"
      variant="contained"
      autoFocus
    >
      Save Changes
    </Button>
  </DialogActions>
</Dialog>

    </Box>
  </>
);
};;


/* ============================================================
   CONTENT TYPE BADGE
   ============================================================ */

interface ContentTypeBadgeProps {
  contentType?: number;
}


const ContentTypeBadge: React.FC<
  ContentTypeBadgeProps
> = ({
  contentType,
}) => {

  /*
   * IMPORTANT:
   *
   * content_type = 0 is Film.
   *
   * Do not use:
   *
   * if (contentType)
   *
   * because 0 is falsy.
   */

  const isFilm =
    contentType === 0;

  const isSeries =
    contentType === 1;


  const label =
    isFilm
      ? 'Film'
      : isSeries
      ? 'Series'
      : `Type ${
          contentType ??
          '—'
        }`;


  return (
    <Chip
      size="small"
      icon={
        isSeries
          ? (
            <TvOutlinedIcon
              style={{
                fontSize: 13,
              }}
            />
          )
          : (
            <MovieOutlinedIcon
              style={{
                fontSize: 13,
              }}
            />
          )
      }
      label={label}
      style={{
        height: 21,
        fontSize: 9,
        fontWeight: 500,
        background:
          '#F1F5F9',
        color:
          '#475569',
      }}
    />
  );
};


/* ============================================================
   LOAD MORE BUTTON
   ============================================================ */

interface LoadMoreButtonProps {
  remainingCount: number;
  onClick: () => void;
}


const LoadMoreButton: React.FC<
  LoadMoreButtonProps
> = ({
  remainingCount,
  onClick,
}) => {

  return (
    <Box
      display="flex"
      justifyContent="center"
      style={{
        padding:
          '5px 0 14px',
      }}
    >

      <Button
        variant="outlined"
        size="small"
        startIcon={
          <ExpandMoreIcon />
        }
        onClick={onClick}
        style={{
          minWidth: 230,
          height: 32,
          borderStyle:
            'dashed',
          borderColor:
            '#7AA7FF',
          color:
            '#2563EB',
          textTransform:
            'none',
          fontSize: 10,
          fontWeight: 600,
          borderRadius: 6,
        }}
      >
        Load More Groups
        {' '}
        (
        {remainingCount}
        {' '}
        remaining)
      </Button>

    </Box>
  );
};


/* ============================================================
   LOADING STATE
   ============================================================ */

interface LoadingStateProps {
  progress: {
    processed: number;
    total?: number;
    remaining?: number;
    page: number;
    status: string;
  };
}

const LoadingState: React.FC<LoadingStateProps> = ({
  progress,
}) => {
  const {
    processed,
    total,
    remaining,
    status,
  } = progress;

  const percentage =
    total && total > 0
      ? Math.min(
          Math.round((processed / total) * 100),
          100
        )
      : 0;

  return (
    <Box
      style={{
        minHeight: 250,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <CircularProgress
        size={32}
        thickness={4}
        variant={
          total
            ? 'determinate'
            : 'indeterminate'
        }
        value={percentage}
      />

      <Typography
        style={{
          marginTop: 14,
          fontSize: 13,
          fontWeight: 600,
          color: '#172033',
          textAlign: 'center',
        }}
      >
        {status}
      </Typography>

      <Typography
        style={{
          marginTop: 7,
          fontSize: 11,
          color: '#64748B',
        }}
      >
        {total !== undefined
          ? `${processed.toLocaleString()} of ${total.toLocaleString()} records processed`
          : `${processed.toLocaleString()} records processed`}
      </Typography>

      {remaining !== undefined && remaining > 0 && (
        <Typography
          style={{
            marginTop: 4,
            fontSize: 11,
            color: '#94A3B8',
          }}
        >
          {remaining.toLocaleString()} records remaining
        </Typography>
      )}

      <Typography
        style={{
          marginTop: 14,
          fontSize: 12,
          color: '#94A3B8',
          textAlign: 'center',
          maxWidth: 350,
        }}
      >
        This may take some time while we compare
        content and identify potential duplicates.
      </Typography>
    </Box>
  );
};

/* ============================================================
   EMPTY STATE
   ============================================================ */

interface EmptyStateProps {
  hasFilters: boolean;
  onClear: () => void;
}


const EmptyState: React.FC<
  EmptyStateProps
> = ({
  hasFilters,
  onClear,
}) => {

  return (
    <Paper
      elevation={0}
      style={{
        minHeight: 230,
        display: 'flex',
        flexDirection:
          'column',
        alignItems:
          'center',
        justifyContent:
          'center',
        border:
          '1px solid #E1E8F0',
        borderRadius: 8,
        background:
          '#FFFFFF',
      }}
    >

      <Box
        style={{
          width: 44,
          height: 44,
          borderRadius:
            '50%',
          display: 'flex',
          alignItems:
            'center',
          justifyContent:
            'center',
          marginBottom: 10,
          background:
            '#EEF6FF',
        }}
      >

        <CheckOutlinedIcon
          style={{
            fontSize: 23,
            color:
              '#2563EB',
          }}
        />

      </Box>


      <Typography
        style={{
          fontSize: 14,
          fontWeight: 600,
          color:
            '#334155',
        }}
      >
        No potential duplicates found
      </Typography>


      <Typography
        style={{
          marginTop: 4,
          fontSize: 10,
          color:
            '#94A3B8',
        }}
      >
        {hasFilters
          ? 'No duplicate groups match the selected filters.'
          : 'All content looks clean for the current scan.'}
      </Typography>


      {hasFilters && (

        <Button
          size="small"
          onClick={onClear}
          style={{
            marginTop: 10,
            color:
              '#2563EB',
            textTransform:
              'none',
            fontSize: 10,
          }}
        >
          Clear filters
        </Button>

      )}

    </Paper>
  );
};

/* ============================================================
   EXPORT
   ============================================================ */

export default DuplicateSpotter;